import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useLookupStore } from "./lookupStore";
import { emptySiteForm } from "../types";
import { isoToDateOnly, dateToISOString } from "../utils/dateHelpers";
import type {
  SiteFormInput,
  PoolFormInput,
  PoolSummary,
  PoolSummaryApiResponse,
} from "../types";

// API client instance - will be injected by components
let apiClient: any = null;

export const setMosquitoApiClient = (client: any) => {
  apiClient = client;
};

// =============================================================================
// CONVERSION HELPERS
// =============================================================================

/**
 * Convert API pool data to UI format (PoolFormInput)
 */
const convertPoolApiToUi = (pool: any) => ({
  poolId: pool.id,
  poolEpiStatus:
    typeof pool.epiStatus === "string"
      ? pool.epiStatus
      : pool.epiStatus?.text || "",
  poolSpecies:
    typeof pool.species === "string" ? pool.species : pool.species?.text || "",
  poolDateCollected: isoToDateOnly(pool.dateCollected),
  poolDateReported: isoToDateOnly(pool.dateReported),
  poolNumberInPool: pool.numberInPool,
  poolIsNotified: pool.notified || false,
  poolActive: pool.active !== false,
  poolCulture: pool.culture || "",
  poolTrapType: pool.trapType || "",
  poolTestingMethod:
    typeof pool.testingMethod === "string"
      ? pool.testingMethod
      : pool.testingMethod?.text || "",
  poolLab: pool.laboratory || "",
  poolLabSpecimenId: pool.labSpecimenId || "",
  poolNote: pool.note || "",
});

interface MosquitoState {
  // Core state
  sites: SiteFormInput[];
  poolSummaries: PoolSummary[];
  loading: boolean;
  error?: string | null;
  selected?: SiteFormInput | null;

  // Core actions
  loadPoolSummaries: () => Promise<void>;
  refreshPoolSummaries: () => Promise<void>;
  loadSiteDetails: (siteId: number) => Promise<SiteFormInput | null>;
  addSiteWithPools: (
    siteInput: SiteFormInput,
    pools: any[]
  ) => Promise<SiteFormInput>;
  updateSiteWithPools: (
    siteId: number,
    siteInput: SiteFormInput,
    pools: any[],
    options?: {
      onProgress?: (current: number, total: number) => void;
      siteIsDirty?: boolean;
      dirtyPoolIndices?: number[];
    }
  ) => Promise<SiteFormInput>;

  // Utility actions
  getById: (id: number) => SiteFormInput | undefined;
  select: (id: number | null) => void;
  resetForm: () => SiteFormInput;
}

const makeApiRequest = async <T>(requestFn: () => Promise<T>): Promise<T> => {
  if (!apiClient) {
    throw new Error("API client not available");
  }
  return await apiClient.authenticatedRequest(requestFn);
};

// =============================================================================
// GENERIC LOOKUP RESOLUTION
// =============================================================================

const MOSQUITO_MODULE_ID = 3;

const resolveLookup = (
  name: string,
  items: { id: number; text: string }[]
): { id: number; text: string } => {
  const found = items.find(
    (item) => item.text.toLowerCase() === name.toLowerCase()
  );
  return found || items[0] || { id: 1, text: name || "Unknown" };
};

const resolveLookupId = (
  name: string,
  items: { id: number; text: string }[]
): number => resolveLookup(name, items).id;

const getOrganizations = () =>
  useLookupStore.getState().getOrganizations(MOSQUITO_MODULE_ID);
const getEpiStatuses = () =>
  useLookupStore.getState().getEpiStatuses(MOSQUITO_MODULE_ID);
const getSpecies = () =>
  useLookupStore.getState().getSpecies(MOSQUITO_MODULE_ID);
const getTestingMethods = () =>
  useLookupStore.getState().getTestingMethods(MOSQUITO_MODULE_ID);

const resolveOrganizationByName = (name: string): number =>
  resolveLookupId(name, getOrganizations());
const resolveEpiStatusByName = (name: string): { id: number; text: string } =>
  resolveLookup(name, getEpiStatuses());
const resolveSpeciesByName = (name: string): { id: number; text: string } =>
  resolveLookup(name, getSpecies());
const resolveTestingMethodByName = (
  name: string
): { id: number; text: string } => resolveLookup(name, getTestingMethods());

// =============================================================================
// STORE
// =============================================================================

export const useMosquitoStore = create<MosquitoState>()(
  devtools((set, get) => {
    return {
      // Initial state
      sites: [],
      poolSummaries: [],
      loading: false,
      error: null,
      selected: null,

      // Core action: Load pool summaries for index page
      loadPoolSummaries: async () => {
        set({ loading: true, error: null });

        try {
          if (apiClient) {
            const summaryData = await makeApiRequest(async () => {
              const mosquitoApi = apiClient.api("api/mosquito");
              const response = await mosquitoApi.get();
              return response?.content || response;
            });

            const summariesArray: PoolSummaryApiResponse[] = Array.isArray(
              summaryData
            )
              ? summaryData
              : summaryData?.content ||
                summaryData?.data ||
                summaryData?.pools ||
                [];

            const summaries: PoolSummaryApiResponse[] = summariesArray;

            // API → UI conversion
            const poolSummaries: PoolSummary[] = summaries.map((summary) => ({
              poolId: summary.id,
              siteId: summary.siteId,
              siteCounty: summary.county || "Unknown",
              siteCity: summary.city || "Unknown",
              siteName: summary.siteName || `Site ${summary.siteId}`,
              poolEpiStatus: summary.epiStatus || "Unknown",
              poolSpecies: summary.species || "Unknown",
              poolDateCollected: isoToDateOnly(summary.dateCollected),
              poolDateReported: isoToDateOnly(summary.dateReported),
            }));

            set({ poolSummaries, loading: false });
            return;
          }

          throw new Error("API client not available");
        } catch (apiError: any) {
          set({
            error: `API error: ${apiError.message}`,
            loading: false,
          });
        }
      },

      refreshPoolSummaries: async () => {
        try {
          if (apiClient) {
            const summaryData = await makeApiRequest(async () => {
              const mosquitoApi = apiClient.api("api/mosquito");
              const response = await mosquitoApi.get();
              return response?.content || response;
            });

            const summariesArray: PoolSummaryApiResponse[] = Array.isArray(
              summaryData
            )
              ? summaryData
              : summaryData?.content ||
                summaryData?.data ||
                summaryData?.pools ||
                [];

            // API → UI conversion
            const poolSummaries: PoolSummary[] = summariesArray.map(
              (summary) => ({
                poolId: summary.id,
                siteId: summary.siteId,
                siteCounty: summary.county || "Unknown",
                siteCity: summary.city || "Unknown",
                siteName: summary.siteName || `Site ${summary.siteId}`,
                poolEpiStatus: summary.epiStatus || "Unknown",
                poolSpecies: summary.species || "Unknown",
                poolDateCollected: isoToDateOnly(summary.dateCollected),
                poolDateReported: isoToDateOnly(summary.dateReported),
              })
            );

            set({ poolSummaries });
          }
        } catch (error: any) {
          console.error("Error refreshing pool summaries:", error);
        }
      },

      // Core action: Load site details
      loadSiteDetails: async (siteId: number) => {
        try {
          const existingInStore = get().sites.find(
            (site) => site.siteId === siteId
          );

          if (apiClient) {
            const siteDetails = await makeApiRequest(async () => {
              const response = await apiClient
                .api(`api/mosquito/${siteId}`)
                .request("GET");

              // Handle wrapped response (content property) or direct response
              const data = response?.content || response;

              if (!data || typeof data !== "object" || !data.id) {
                throw new Error("Invalid site data received from API");
              }

              return data;
            });

            // API → UI conversion for pools, sorted by ID ascending so newest pools are last
            // (display reverses them so newest appear first)
            const convertedPools = (siteDetails.pools || [])
              .map(convertPoolApiToUi)
              .sort(
                (a: PoolFormInput, b: PoolFormInput) =>
                  (a.poolId || 0) - (b.poolId || 0)
              );

            // API → UI conversion for site
            const record: SiteFormInput = {
              siteId: siteDetails.id,
              siteName:
                siteDetails.name ||
                (siteDetails.id ? `Site ${siteDetails.id}` : "Unknown Site"),
              siteOrganization: siteDetails.organization?.text || "Unknown",
              siteAddress: siteDetails.streetAddress || "",
              siteCity: siteDetails.city || "",
              siteState:
                siteDetails.state?.abbreviation || siteDetails.state?.id || "",
              siteZip: siteDetails.zip || "",
              siteCounty:
                siteDetails.county?.name || siteDetails.county?.id || "Unknown",
              siteIsGeolocated: siteDetails.isAddressValidated ?? false,
              siteActive: siteDetails.active !== false,
              siteLatitude: siteDetails.latitude,
              siteLongitude: siteDetails.longitude,
              siteGpsNote:
                existingInStore?.siteGpsNote || siteDetails.gpsNote || "",
              sitePrimaryContactFirstName:
                siteDetails.primaryContactFirstName || "",
              sitePrimaryContactLastName:
                siteDetails.primaryContactLastName || "",
              sitePrimaryContactPhone:
                siteDetails.primaryContactPhoneNumber || "",
              sitePrimaryContactEmail: siteDetails.primaryContactEmail || "",
              siteSecondaryContactFirstName:
                siteDetails.secondaryContactFirstName || "",
              siteSecondaryContactLastName:
                siteDetails.secondaryContactLastName || "",
              siteSecondaryContactPhone:
                siteDetails.secondaryContactPhoneNumber || "",
              siteSecondaryContactEmail:
                siteDetails.secondaryContactEmail || "",
              siteComment:
                existingInStore?.siteComment || siteDetails.comment || "",
              pools: convertedPools,
            };

            // Update store
            set((s) => {
              const existingIndex = s.sites.findIndex(
                (site) => site.siteId === siteId
              );
              if (existingIndex >= 0) {
                const newSites = [...s.sites];
                newSites[existingIndex] = record;
                return { sites: newSites };
              } else {
                return { sites: [...s.sites, record] };
              }
            });

            return record;
          }

          return null;
        } catch (error: any) {
          console.error(`❌ Error loading site ${siteId}:`, error);
          set({ error: `Failed to load site: ${error.message}` });
          return null;
        }
      },

      // Core action: Add new site with pools
      addSiteWithPools: async (siteInput: SiteFormInput, pools: any[]) => {
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const lookupState = useLookupStore.getState();
        const state = lookupState.getStateByAbbreviation(
          siteInput.siteState || ""
        );
        const county = lookupState.getCountyByName(siteInput.siteCounty);

        if (!state || !county) {
          throw new Error(
            `Invalid state: ${siteInput.siteState} or county: ${siteInput.siteCounty}`
          );
        }

        // UI → API conversion (single conversion point)
        const apiPayload = {
          organizationId: resolveOrganizationByName(siteInput.siteOrganization),
          name: siteInput.siteName,
          streetAddress: siteInput.siteAddress,
          city: siteInput.siteCity,
          stateId: state.id,
          countyId: county.id,
          zip: siteInput.siteZip,
          isAddressValidated: siteInput.siteIsGeolocated ?? false,
          active: siteInput.siteActive !== false,
          latitude: siteInput.siteLatitude,
          longitude: siteInput.siteLongitude,
          gpsNote: siteInput.siteGpsNote,
          primaryContactFirstName: siteInput.sitePrimaryContactFirstName,
          primaryContactLastName: siteInput.sitePrimaryContactLastName,
          primaryContactPhoneNumber: siteInput.sitePrimaryContactPhone,
          primaryContactEmail: siteInput.sitePrimaryContactEmail,
          secondaryContactFirstName: siteInput.siteSecondaryContactFirstName,
          secondaryContactLastName: siteInput.siteSecondaryContactLastName,
          secondaryContactPhoneNumber: siteInput.siteSecondaryContactPhone,
          secondaryContactEmail: siteInput.siteSecondaryContactEmail,
          comment: siteInput.siteComment,
          pools: pools.map((pool) => ({
            dateCollected:
              dateToISOString(pool.poolDateCollected) ||
              new Date().toISOString(),
            numberInPool: Number(pool.poolNumberInPool) || 0,
            dateReported:
              dateToISOString(pool.poolDateReported) ||
              new Date().toISOString(),
            active: pool.poolActive ?? true,
            culture: pool.poolCulture || null,
            trapType: pool.poolTrapType || null,
            labSpecimenId: pool.poolLabSpecimenId || null,
            note: pool.poolNote || null,
            laboratory: pool.poolLab || null,
            epiStatusId: resolveEpiStatusByName(pool.poolEpiStatus || "Unknown")
              .id,
            speciesId: resolveSpeciesByName(pool.poolSpecies || "Unknown").id,
            testingMethodId: resolveTestingMethodByName(
              pool.poolTestingMethod || "Unknown"
            ).id,
          })),
        };

        const result = await makeApiRequest(async () => {
          return await apiClient
            .api("api/mosquito")
            .request("POST", apiPayload);
        });

        const responseId = result?.content?.id || result?.id;
        const responsePools = result?.content?.pools || result?.pools || [];

        // API → UI conversion for pools
        const convertedPools = (responsePools || []).map(convertPoolApiToUi);

        const record: SiteFormInput = {
          ...siteInput,
          siteId: responseId,
          pools: convertedPools,
        };

        // Update stores
        set((s) => ({
          sites: [...s.sites, record],
          poolSummaries: [
            ...s.poolSummaries,
            ...(responsePools || []).map((pool: any) => ({
              poolId: pool.id,
              siteId: responseId,
              siteCounty: siteInput.siteCounty,
              siteCity: siteInput.siteCity,
              siteName: siteInput.siteName,
              poolEpiStatus: pool.epiStatus?.text || "Unknown",
              poolSpecies: pool.species?.text || "Unknown",
              poolDateCollected: isoToDateOnly(pool.dateCollected),
              poolDateReported: isoToDateOnly(pool.dateReported),
            })),
          ],
        }));

        await get().refreshPoolSummaries();

        return record;
      },

      // Core action: Update site with pools
      updateSiteWithPools: async (
        siteId: number,
        siteInput: SiteFormInput,
        pools: any[],
        options?: {
          onProgress?: (current: number, total: number) => void;
          siteIsDirty?: boolean;
          dirtyPoolIndices?: number[];
        }
      ) => {
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const {
          onProgress,
          siteIsDirty = true,
          dirtyPoolIndices,
        } = options || {};

        const lookupState = useLookupStore.getState();
        const state = lookupState.getStateByAbbreviation(
          siteInput.siteState || ""
        );
        const county = lookupState.getCountyByName(siteInput.siteCounty);

        if (!state || !county) {
          throw new Error(
            `Invalid state: ${siteInput.siteState} or county: ${siteInput.siteCounty}`
          );
        }

        // Update site data only if dirty - UI → API conversion
        if (siteIsDirty) {
          const siteUpdatePayload = {
            id: siteId,
            organizationId: resolveOrganizationByName(
              siteInput.siteOrganization
            ),
            name: siteInput.siteName,
            streetAddress: siteInput.siteAddress,
            city: siteInput.siteCity,
            stateId: state.id,
            countyId: county.id,
            zip: siteInput.siteZip,
            isAddressValidated: siteInput.siteIsGeolocated ?? false,
            active: siteInput.siteActive !== false,
            latitude: siteInput.siteLatitude,
            longitude: siteInput.siteLongitude,
            gpsNote: siteInput.siteGpsNote,
            primaryContactFirstName: siteInput.sitePrimaryContactFirstName,
            primaryContactLastName: siteInput.sitePrimaryContactLastName,
            primaryContactPhoneNumber: siteInput.sitePrimaryContactPhone,
            primaryContactEmail: siteInput.sitePrimaryContactEmail,
            secondaryContactFirstName: siteInput.siteSecondaryContactFirstName,
            secondaryContactLastName: siteInput.siteSecondaryContactLastName,
            secondaryContactPhoneNumber: siteInput.siteSecondaryContactPhone,
            secondaryContactEmail: siteInput.siteSecondaryContactEmail,
            comment: siteInput.siteComment,
          };

          await makeApiRequest(async () => {
            const response = await apiClient
              .api(`api/mosquito/${siteId}`)
              .request("PATCH", siteUpdatePayload);

            // Check if response is an error
            if (!response.ok) {
              throw response;
            }

            return response;
          });
        }

        // Filter pools
        const validPools = pools.filter((pool, index) => {
          const hasRequiredFields =
            pool.poolDateCollected &&
            pool.poolDateReported &&
            pool.poolEpiStatus &&
            pool.poolSpecies &&
            pool.poolTestingMethod;

          const isDirty =
            dirtyPoolIndices === undefined || dirtyPoolIndices.includes(index);

          return hasRequiredFields && isDirty;
        });

        const totalOperations = (siteIsDirty ? 1 : 0) + validPools.length;
        let completedOperations = siteIsDirty ? 1 : 0;
        onProgress?.(completedOperations, totalOperations);

        const successfulPools: PoolFormInput[] = [];

        for (const pool of validPools) {
          const getIdFromObject = (
            obj: any,
            fieldName: string
          ): number | undefined => {
            if (typeof obj === "object" && obj && obj.id) {
              return Number(obj.id);
            } else if (typeof obj === "string") {
              let resolved;
              switch (fieldName) {
                case "poolEpiStatus":
                  resolved = resolveEpiStatusByName(obj);
                  break;
                case "poolSpecies":
                  resolved = resolveSpeciesByName(obj);
                  break;
                case "poolTestingMethod":
                  resolved = resolveTestingMethodByName(obj);
                  break;
              }
              return resolved?.id ? Number(resolved.id) : undefined;
            } else if (typeof obj === "number") {
              return obj;
            }
            return undefined;
          };

          const epiStatusId = getIdFromObject(
            pool.poolEpiStatus,
            "poolEpiStatus"
          );
          const speciesId = getIdFromObject(pool.poolSpecies, "poolSpecies");
          const testingMethodId = getIdFromObject(
            pool.poolTestingMethod,
            "poolTestingMethod"
          );

          // UI → API conversion for pool
          const poolPayload = {
            id: pool.poolId,
            epiStatusId,
            speciesId,
            testingMethodId,
            dateCollected:
              dateToISOString(pool.poolDateCollected) ||
              new Date().toISOString(),
            numberInPool: Number(pool.poolNumberInPool) || 0,
            dateReported:
              dateToISOString(pool.poolDateReported) ||
              new Date().toISOString(),
            active: pool.poolActive ?? true,
            culture: pool.poolCulture || "",
            trapType: pool.poolTrapType || "",
            labSpecimenId: pool.poolLabSpecimenId || "",
            note: pool.poolNote || "",
            laboratory: pool.poolLab || "",
          };

          if (
            pool.poolId &&
            !isNaN(Number(pool.poolId)) &&
            Number(pool.poolId) > 0
          ) {
            // Update existing pool
            const updateResult = await makeApiRequest(async () => {
              const response = await apiClient
                .api(`api/mosquito/pools/${pool.poolId}`)
                .request("PATCH", poolPayload);
              return response;
            });

            if (
              !updateResult ||
              (!updateResult.ok && updateResult.status >= 400)
            ) {
              // Throw the response so parseFormError can extract error details
              throw updateResult;
            }
          } else {
            // Create new pool
            const createResult = await makeApiRequest(async () => {
              const response = await apiClient
                .api(`api/mosquito/${siteId}/pools`)
                .request("POST", poolPayload);
              return response;
            });

            // Check if response is an error
            if (
              !createResult ||
              (!createResult.ok && createResult.status >= 400)
            ) {
              throw createResult;
            }

            // Extract the new pool data
            const newPool = createResult?.content || createResult;
            if (newPool && newPool.id) {
              const oldId = pool.poolId;
              pool.poolId = newPool.id;

              set((s) => ({
                poolSummaries: s.poolSummaries.map((summary) =>
                  summary.poolId === oldId && summary.siteId === siteId
                    ? { ...summary, poolId: newPool.id }
                    : summary
                ),
              }));
            } else {
              throw new Error("Pool creation returned invalid ID");
            }
          }

          successfulPools.push(pool);
          completedOperations++;
          onProgress?.(completedOperations, totalOperations);
        }

        const updatedSiteRecord: SiteFormInput = {
          ...siteInput,
          siteId: siteId,
          pools: successfulPools,
        };

        set((s) => ({
          sites: s.sites.map((site) =>
            site.siteId === siteId ? updatedSiteRecord : site
          ),
        }));

        const newPoolSummaries: PoolSummary[] = successfulPools
          .filter(
            (pool): pool is PoolFormInput & { poolId: number } =>
              pool.poolId !== undefined
          )
          .map((pool) => ({
            poolId: pool.poolId,
            siteId: siteId,
            siteCounty: siteInput.siteCounty,
            siteCity: siteInput.siteCity,
            siteName: siteInput.siteName,
            poolEpiStatus:
              typeof pool.poolEpiStatus === "string"
                ? pool.poolEpiStatus
                : (pool.poolEpiStatus as any)?.text || "Unknown",
            poolSpecies:
              typeof pool.poolSpecies === "string"
                ? pool.poolSpecies
                : (pool.poolSpecies as any)?.text || "Unknown",
            poolDateCollected: isoToDateOnly(pool.poolDateCollected),
            poolDateReported: isoToDateOnly(pool.poolDateReported),
          }));

        set((s) => ({
          poolSummaries: [
            ...s.poolSummaries.filter((p) => p.siteId !== siteId),
            ...newPoolSummaries,
          ],
        }));

        return updatedSiteRecord;
      },

      // Utility functions
      getById: (id: number) => get().sites.find((site) => site.siteId === id),

      select: (id: number | null) =>
        set({ selected: id ? get().getById(id) : null }),

      resetForm: () => ({ ...emptySiteForm }),
    };
  })
);
